import { memo, useEffect, useRef, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  DocumentViewer,
  MiButton,
  MiDetailFields,
  MiIcon,
  MiLoader,
  MiModalConfirm,
} from "@miplatform/components";
import {
  ATTEMPT_STATUSES,
  BUTTON_LABELS,
  BUTTON_VARIANTS,
  DATE_TIME_FORMATS,
  ICONS_WEB,
  INPUT_TYPES,
  ISSUE_TYPES,
} from "@miplatform/constants";
import { useFeatureFlags } from "@miplatform/feature-flags";
import { useBoolean, useEdit } from "@miplatform/hooks";
import { Colors } from "@miplatform/types";
import { getUniqueObjects, toFormattedDate } from "@miplatform/utils";
import * as mdi from "@mdi/js";
import { Box } from "@mui/material";
import { AreaAutoComplete } from "../../../../../../../components/AreaAutoComplete/AreaAutoComplete";
import {
  CLOSEOUT_SCREENS as SCREENS,
  CLOSING_OUT_VIEWS as VIEWS,
} from "../../../constants";
import CloseoutMaterial from "../../CloseoutMaterial/CloseoutMaterial";
import { IssueHistoryHeader } from "../IssueHistoryHeader";
import { IssueUsersHeader } from "../IssueUsersHeader";
import { IssueReviewContent } from "./IssueReviewContent";
import { RichIssueReviewContent } from "./RichIssueReviewContent";
import { useClosingoutViewModel } from "./useClosingoutViewModel";

const SECTIONS = {
  DIAGNOSED_ISSUE: "Technicians Diagnostics (Before Repair Attempt)",
  REPAIR_DETAILS: "Repair Details (After Repair Attempt)",
  TASK_DESCRIPTION: "Task Description",
  RESOLUTION: "Resolution",
  TASK_DETAILS: "Task Details",
};

const INITIAL_EDIT = {
  issueDescription: "",
  repairDetails: "",
  repairStatus: "",
  diagnosedIssue: "",
  reviewDescription: "",
  billable: "",
  incompleteReason: "",
  verificationRequest: "",
  verificationResponse: "",
};

const AugmentedOption = ({ icon, color, text }) => (
  <Box display="flex" alignItems="center">
    <MiIcon sx={{ marginRight: 1 }} size={0.7} path={icon} color={color} />
    <Box sx={{ width: "80%", marginLeft: "4px" }}>{text}</Box>
  </Box>
);

export const Closingout = memo(
  ({
    issueIndex,
    readOnly = false,
    setIssueIndex,
    attempt = {},
    selectedIssue = null,
    setActiveScreen,
    setIsWoReviewed = () => {},
    setSelectedIssue = () => {},
    view: initialView = VIEWS.ISSUE_REVIEW,
    getAttempt,
    onCancel = () => null,
    isLoading,
    users = [],
    tenant,
    verificationReview = false,
    completeVerification = async () => false,
  }) => {
    const [completedIssueCount, setCompletedIssueCount] = useState(0);
    const [initialEdit, setInitialEdit] = useState(INITIAL_EDIT);
    const [view, setView] = useState(initialView);
    const [selectedIssueResult, setSelectedIssueResult] = useState();
    const [isMaterialChecked, setIsMaterialChecked] = useState(false);
    const contentRef = useRef(null);
    const [confirmModalConfig, setConfirmModalConfig] = useState({
      open: false,
      title: "",
      titleIcon: null,
      description: "",
      textPositive: BUTTON_LABELS.CONFIRM,
      textNegative: BUTTON_LABELS.CANCEL,
      handlePositive: () => {},
      handleNegative: () =>
        setConfirmModalConfig((prev) => ({ ...prev, open: false })),
    });

    const { issues = [] } = attempt;
    const { classes, theme } = useStyles({
      history: attempt.attemptNumber > 1,
    });
    const { featureFlags } = useFeatureFlags();
    const isMiDayFeatureEnabled = featureFlags?.miDayEnabled;
    const issueCount = issues.length || 0;
    const isLastOne = issueIndex + 1 === issueCount;

    const materials = issues.reduce((acc, issue) => {
      return [
        ...acc,
        ...(issue.materialReferences || []).map((material) => ({
          ...material,
          issueId: issue.id,
        })),
      ];
    }, []);

    const {
      ISSUE_RESOLUTION_OPTIONS,
      COMPLETED_STATUSES,
      issueImages,
      repairImages,
      showImagesViewer,
      imagesInViewer,
      issueTemplates,
      assessImages,
      setAssessImages,
      reviewImages,
      setReviewImages,
      setImagesInViewer,
      setShowImagesViewer,
      setIssueImages,
      setRepairImages,
      handleAddIssueImage,
      handleAddRepairImage,
      handleAddAssessImage,
      handleAddReviewImage,
      removeIssueImage,
      removeRepairImage,
      removeAssessImage,
      removeReviewImage,
      reviewIssue,
      filterIssueFiles,
      isStatusRepair,
      getTaskResolutionStatus,
      getIssueTemplates,
      currentIssue,
      currentIssueResult,
      loading,
      isAddingImages,
      isCheckedStep,
      setIsCheckedStep,
    } = useClosingoutViewModel({
      orderTemplateId:
        attempt.orderTemplateId || attempt.assignment?.order?.orderTemplateId,
      getAttempt,
      attempt,
      issueIndex,
      selectedIssue,
    });
    const { title = "--", originalDescription: description = "--" } =
      currentIssue.issueResult || currentIssue;
    const [
      isIssueImageViewer,
      turnOnIssueImageViewer,
      turnOffIssueImageViewer,
    ] = useBoolean();

    const edit = useEdit(initialEdit);
    const isTask =
      currentIssue.type === ISSUE_TYPES.TASK ||
      currentIssue.type === ISSUE_TYPES.CAMERA_LINE;

    const isAttemptOpen = ![
      ATTEMPT_STATUSES.CLOSEOUT_IN_PROGRESS,
      ATTEMPT_STATUSES.CLOSED,
      ATTEMPT_STATUSES.SUBMITTED,
    ].includes(attempt.attemptStatus);

    const scrollToTop = () => {
      let scrollContainer = contentRef.current?.parentElement;

      while (scrollContainer) {
        const { overflowY } = window.getComputedStyle(scrollContainer);
        const hasScrollableOverflow = /(auto|scroll|overlay)/.test(overflowY);

        if (
          hasScrollableOverflow &&
          scrollContainer.scrollHeight > scrollContainer.clientHeight
        ) {
          break;
        }

        scrollContainer = scrollContainer.parentElement;
      }

      if (!scrollContainer) return;

      window.requestAnimationFrame(() => {
        scrollContainer.scrollTo({ top: 0, behavior: "smooth" });
      });
    };

    useEffect(() => {
      edit.reset();

      const {
        resolvingDocuments,
        originatedDocuments,
        accessDocuments,
        reviewDocuments,
      } = filterIssueFiles(
        getUniqueObjects(currentIssueResult?.documents || [])
      );

      setIssueImages(originatedDocuments);
      setRepairImages(resolvingDocuments);
      setReviewImages(reviewDocuments);
      setAssessImages(accessDocuments);

      const {
        description: issueDescription = "",
        repairDescription: repairDetails = "",
        originalDescription = "",
        areaName = "",
        issueTemplateId = "",
        diagnosedIssue = null,
        reviewDescription = "",
        verificationRequest = "",
        verificationResponse = "",
        isBillable,
      } = currentIssueResult || {};
      setInitialEdit({
        issueDescription,
        repairDetails,
        repairStatus: getTaskResolutionStatus(currentIssueResult || {}),
        diagnosedIssue,
        issueTemplateId:
          issueTemplateId || currentIssue.issueTemplateId || null,
        areaName: areaName || currentIssue.areaName || "",
        originalDescription:
          originalDescription || currentIssue.originalDescription || "",
        reviewDescription,
        billable: String(isBillable ?? currentIssue.isBillable ?? ""),
        incompleteReason: currentIssueResult?.incompleteReason ?? "",
        verificationRequest,
        verificationResponse,
      });
    }, [currentIssue, issueIndex, currentIssueResult]);

    const allFields = [
      {
        label: "Diagnosed Task",
        name: "diagnosedIssue",
        required: !isAttemptOpen,
        fieldType: INPUT_TYPES.SELECT,
        options: issueTemplates?.map((x) => ({ label: x.name, value: x.name })),
        width: "100%",
        style: { paddingLeft: 0, paddingRight: 0 },
      },
      {
        label: "Task Description",
        name: "issueDescription",
        required: !isAttemptOpen,
        width: "100%",
        multiline: true,
        minRows: 3,
        maxRows: 10,
        style: { paddingLeft: 0, paddingRight: 0 },
      },
      {
        label: "Repair Details",
        name: "repairDetails",
        required: !isAttemptOpen,
        width: "100%",
        multiline: true,
        minRows: 3,
        maxRows: 10,
        style: { paddingLeft: 0, paddingRight: 0 },
      },
      {
        label: "Repair and Visit Status?",
        name: "repairStatus",
        required: !isAttemptOpen,
        fieldType: INPUT_TYPES.SELECT,
        options: Object.keys(ISSUE_RESOLUTION_OPTIONS).map((optionKey) => ({
          label: (
            <AugmentedOption
              {...{
                ...ISSUE_RESOLUTION_OPTIONS[optionKey],
                text: isTask
                  ? ISSUE_RESOLUTION_OPTIONS[optionKey].text.replace(
                      "Repair",
                      ""
                    )
                  : ISSUE_RESOLUTION_OPTIONS[optionKey].text,
              }}
            />
          ),
          value: ISSUE_RESOLUTION_OPTIONS[optionKey].value,
        })),
        style: {
          paddingLeft: 0,
          paddingRight: 0,
          "& .MuiInputLabelRoot": {
            color: isStatusRepair(edit.getValue("repairStatus"))
              ? "primary"
              : "red",
          },
        },
        InputProps: {
          error: !isStatusRepair(edit.getValue("repairStatus")),
        },
        disabled:
          isAttemptOpen || ATTEMPT_STATUSES.CLOSED === attempt.attemptStatus,
      },
      {
        label: "Task Title",
        name: "issueTemplateId",
        fieldType: INPUT_TYPES.SELECT,
        options: issueTemplates?.map((x) => ({ label: x.name, value: x.id })),
        style: isTask ? { display: "none" } : null,
        padding: theme.spacing(1, 1, 0, 0),
        required: true,
        readOnly: isTask,
      },
      {
        label: "Area",
        name: "areaName",
        fieldType: INPUT_TYPES.CUSTOM,
        customComponent: (
          <AreaAutoComplete
            onChange={(value) => {
              edit.update({ areaName: value });
            }}
            value={edit.getValue("areaName") || initialEdit.areaName}
            required={!isTask}
          />
        ),
        style: { paddingLeft: 0, paddingRight: 0 },
      },
      {
        label: "Description",
        name: "originalDescription",
        multiline: true,
        required: true,
        rows: 2,
        width: "100%",
        padding: theme.spacing(1, 0, 2, 0),
      },
    ];

    const getFields = ({ section, disabled }) => {
      switch (section) {
        case SECTIONS.DIAGNOSED_ISSUE:
          return allFields
            .slice(0, 1)
            .map((x) => ({ ...x, readOnly: disabled || x.readOnly }));
        case SECTIONS.TASK_DESCRIPTION:
          return allFields
            .slice(1, 2)
            .map((x) => ({ ...x, readOnly: disabled || x.readOnly }));
        case SECTIONS.REPAIR_DETAILS:
          return allFields
            .slice(2, 3)
            .map((x) => ({ ...x, readOnly: disabled || x.readOnly }));
        case SECTIONS.RESOLUTION:
          return allFields
            .slice(3, 4)
            .map((x) => ({ ...x, readOnly: disabled || x.readOnly }));
        case SECTIONS.TASK_DETAILS:
          return allFields
            .slice(4, 6)
            .map((x) => ({ ...x, readOnly: disabled || x.readOnly }));
        default:
          return [];
      }
    };

    const isFormValid = (() => {
      if (selectedIssueResult) {
        return true;
      }

      if (selectedIssue && isAttemptOpen) {
        if (isTask) {
          return edit.allFilled("originalDescription");
        }
        return edit.allFilled(
          "issueTemplateId",
          "areaName",
          "originalDescription"
        );
      }

      if (
        edit.getValue("repairStatus") ===
        ISSUE_RESOLUTION_OPTIONS.REPAIRED.value
      ) {
        if (isTask) return edit.allFilled("issueDescription", "repairStatus");
        return edit.allFilled(
          "issueDescription",
          "repairDetails",
          "repairStatus",
          "diagnosedIssue"
        );
      }

      return edit.allFilled("issueDescription", "repairStatus");
    })();

    const handleDeleteDocument = (document, index) => {
      const handleModalClose = () =>
        setConfirmModalConfig((prev) => ({ ...prev, open: false }));
      const imageGroups = [
        [issueImages, removeIssueImage],
        [repairImages, removeRepairImage],
        [assessImages, removeAssessImage],
        [reviewImages, removeReviewImage],
      ];
      const removeImage =
        imageGroups.find(([images]) =>
          images.some(
            (image) =>
              image === document ||
              (document.id && image.id === document.id) ||
              (document.tempURL && image.tempURL === document.tempURL)
          )
        )?.[1] || (isIssueImageViewer ? removeIssueImage : removeRepairImage);

      setConfirmModalConfig({
        open: true,
        title: "Delete Document",
        titleIcon: {
          path: mdi[ICONS_WEB.TRASH_CAN],
          color: theme.palette.error.main,
        },
        textPositive: BUTTON_LABELS.DELETE,
        buttonPositiveColor: Colors.Error,
        description:
          "You are about to delete this document. Are you sure you want to continue?",
        handlePositive: () => {
          removeImage(document, index);
          handleModalClose();
        },
        handleNegative: handleModalClose,
        ...((selectedIssueResult || readOnly) && {
          hideNegative: true,
          buttonPositiveColor: Colors.Primary,
          textPositive: BUTTON_LABELS.OK,
          description: "Task attachments cannot be modified after billing.",
          handlePositive: handleModalClose,
        }),
      });
    };

    const onSubmit = async ({
      hasModalOpen = false,
      onVerificationAction,
    } = {}) => {
      scrollToTop();

      if (readOnly) {
        if (!isLastOne && !selectedIssue) {
          setIssueIndex((prev) => prev + 1);
        }
        return;
      }

      if (view === VIEWS.MATERIAL_INVOICE) {
        if (onVerificationAction) {
          await onVerificationAction();
        } else {
          setActiveScreen(SCREENS.REVIEW);
        }
        return;
      }

      if (selectedIssue && !hasModalOpen) {
        setConfirmModalConfig({
          open: true,
          title: "Save Changes",
          titleIcon: {
            path: mdi[ICONS_WEB.SAVE],
            color: theme.palette.primary.main,
          },
          textPositive: "Yes, Save",
          description:
            "You are about to save changes made to the task description. Are you sure you would like to continue?",
          handlePositive: () => onSubmit({ hasModalOpen: true }),
          handleNegative: () =>
            setConfirmModalConfig((prev) => ({ ...prev, open: false })),
        });

        return;
      }

      const {
        issueDescription,
        repairDetails,
        repairStatus,
        diagnosedIssue,
        originalDescription,
        reviewDescription,
        issueTemplateId,
        areaName,
        billable,
        incompleteReason,
        verificationRequest,
        verificationResponse,
      } = edit.getValue();
      const continueAfterReview = async () => {
        setIsCheckedStep(false);

        if (!isLastOne && !selectedIssue) {
          setIssueIndex((prev) => prev + 1);
        }
        if (onVerificationAction) {
          await onVerificationAction();
          return;
        }

        if (selectedIssue) {
          setSelectedIssue(null);
          setActiveScreen(SCREENS.MAIN);
          setConfirmModalConfig((prev) => ({ ...prev, open: false }));
          return;
        }

        if (
          view !== VIEWS.MATERIAL_INVOICE &&
          isMiDayFeatureEnabled &&
          isLastOne &&
          !attempt?.assignment?.order?.hasCameraLine
        ) {
          setView(VIEWS.MATERIAL_INVOICE);
          return;
        }

        if (isLastOne && !selectedIssue) {
          setActiveScreen(SCREENS.REVIEW);
        }

        const isCompleted = COMPLETED_STATUSES.includes(
          edit.edits.repairStatus ?? getTaskResolutionStatus(currentIssue)
        )
          ? 1
          : 0;

        const nextCompleteTaskCountValue = completedIssueCount + isCompleted;

        setCompletedIssueCount(nextCompleteTaskCountValue);

        if (isLastOne) {
          const needsReview = nextCompleteTaskCountValue < issues.length;

          setIsWoReviewed(needsReview);
          setActiveScreen(SCREENS.REVIEW);
          return;
        }

        setView(VIEWS.ISSUE_REVIEW);
      };

      await reviewIssue({
        issue: currentIssue,
        attemptId: attempt.id,
        assignmentId: attempt.assignment.id,
        userId: attempt.userId,
        orderId: attempt.orderId,
        issueDescription,
        repairDetails,
        repairStatus,
        diagnosedIssue,
        originalDescription,
        reviewDescription,
        issueTemplateId,
        areaName,
        isBillable: billable === "true",
        incompleteReason,
        verificationRequest,
        verificationResponse,
        onReviewApproved: continueAfterReview,
      });
    };

    const getNextButtonText = () => {
      // Handle non-closeout state
      if (selectedIssue) {
        return "Save Changes";
      }

      // Handle last issue during closeout
      if (isLastOne) {
        if (
          view !== VIEWS.MATERIAL_INVOICE &&
          isMiDayFeatureEnabled &&
          !attempt?.assignment?.order?.hasCameraLine
        ) {
          return "Review Materials";
        }
        return "Review";
      }

      // Handle intermediate issues during closeout
      return `${readOnly ? "Task" : "Start: Closeout"} ${
        issueIndex + 2
      }/${issueCount}`;
    };
    const getReturnLabel = (verificationReview, readOnly) => {
      if (verificationReview) {
        return "Review";
      }
      return readOnly ? "Task" : "Closeout";
    };

    const isMaterialInvoiceView = view === VIEWS.MATERIAL_INVOICE;
    const isStepApprovalRequired =
      isMiDayFeatureEnabled && !isMaterialInvoiceView;
    const isActionLoading = isLoading || loading || isAddingImages;

    const isNextButtonDisabled =
      isAddingImages ||
      (!readOnly &&
        ((!isMaterialInvoiceView && !isFormValid && !selectedIssue) ||
          (isStepApprovalRequired && !isCheckedStep && !selectedIssue) ||
          (isMaterialInvoiceView &&
            !isMaterialChecked &&
            materials.length !== 0)));

    const handleCompleteVerification = async () => {
      const isUpdated = await completeVerification();

      if (isUpdated) {
        setConfirmModalConfig((prev) => ({ ...prev, open: false }));
        onCancel();
      }
    };

    const handleCompleteClick = () => {
      setConfirmModalConfig({
        open: true,
        title: "Complete Verification Review",
        titleIcon: {
          path: mdi[ICONS_WEB.SAVE],
          color: theme.palette.primary.main,
        },
        textPositive: "Complete review",
        description: "Would you like to complete this verification review?",
        handlePositive: () =>
          onSubmit({
            hasModalOpen: true,
            onVerificationAction: handleCompleteVerification,
          }),
        handleNegative: () =>
          setConfirmModalConfig((prev) => ({
            ...prev,
            open: false,
          })),
      });
    };

    useEffect(() => {
      if (!issueTemplates) {
        getIssueTemplates();
      }
    }, [attempt, issueTemplates]);

    const historyEdit = useEdit({
      ...selectedIssueResult,
      billable: String(selectedIssueResult?.isBillable ?? ""),
      issueDescription: selectedIssueResult?.description,
      repairDetails: selectedIssueResult?.repairDescription,
      repairStatus: selectedIssueResult
        ? getTaskResolutionStatus(selectedIssueResult)
        : undefined,
    });

    const {
      resolvingDocuments: selectedIssueResultResolvingDocuments = [],
      originatedDocuments: selectedIssueResultOriginatedDocuments = [],
    } = filterIssueFiles(selectedIssueResult?.documents || []);

    const IssueReviewComponent = isMiDayFeatureEnabled
      ? RichIssueReviewContent
      : IssueReviewContent;

    return (
      <>
        <Box ref={contentRef} className={classes.contentContainer}>
          {!selectedIssue &&
            (attempt.previousAttemptId ||
              attempt?.nextAttemptIds?.length > 0) && (
              <IssueHistoryHeader
                isCurrentAttempt={false}
                issueResult={selectedIssueResult}
                currentIssue={currentIssue}
                navigate={setSelectedIssueResult}
                attempt={attempt}
                lastUpdatedDate={currentIssue?.modifiedOn}
                attemptNumber={attempt?.attemptNumber}
              />
            )}

          <Box className={classes.content}>
            {selectedIssue && (
              <Box className={classes.header}>
                <Box className={classes.headerLeftBox}>Editing: Task</Box>
                <Box className={classes.headerRightBox}>
                  Task Last Updated:{" "}
                  {toFormattedDate(
                    currentIssue?.modifiedOn,
                    DATE_TIME_FORMATS.FORMAT_MMM_D_YYYY_H_MM_A
                  )}
                </Box>
              </Box>
            )}

            <Box style={{ padding: theme.spacing(1, 3) }}>
              {!selectedIssue && view !== VIEWS.MATERIAL_INVOICE && (
                <IssueUsersHeader
                  title={
                    <>
                      {issueIndex + 1}.&nbsp;
                      {`Closeout: ${title}`}
                    </>
                  }
                  attempt={attempt}
                  issue={currentIssue}
                  users={users}
                />
              )}
              {selectedIssueResult && historyEdit && (
                <IssueReviewComponent
                  disabled
                  description={description}
                  edit={historyEdit}
                  isTask={isTask}
                  getFields={getFields}
                  sections={SECTIONS}
                  issueImages={selectedIssueResultOriginatedDocuments}
                  repairImages={selectedIssueResultResolvingDocuments}
                  reviewImages={reviewImages}
                  assessImages={assessImages}
                  setShowImagesViewer={setShowImagesViewer}
                  setImagesInViewer={setImagesInViewer}
                  onShowIssueImageViewer={turnOnIssueImageViewer}
                  onShowRepairImageViewer={turnOffIssueImageViewer}
                  handleAddIssueImage={() => {}}
                  handleAddRepairImage={() => {}}
                  currentIssue={currentIssue}
                  assignedUserId={attempt.assignedUserId}
                  handleAddAssessImage={() => {}}
                  handleAddReviewImage={() => {}}
                />
              )}

              {view === VIEWS.ISSUE_REVIEW && !selectedIssueResult && (
                <Box>
                  {selectedIssue && (
                    <MiDetailFields
                      detailFields={getFields({
                        section: SECTIONS.TASK_DETAILS,
                      })}
                      edit={edit}
                    />
                  )}
                  <MiLoader type="linear" isLoading={isLoading} />
                  <IssueReviewComponent
                    disabled={readOnly}
                    edit={edit}
                    getFields={getFields}
                    sections={SECTIONS}
                    issueImages={issueImages}
                    reviewImages={reviewImages}
                    assessImages={assessImages}
                    repairImages={repairImages}
                    setShowImagesViewer={setShowImagesViewer}
                    setImagesInViewer={setImagesInViewer}
                    handleAddIssueImage={handleAddIssueImage}
                    handleAddRepairImage={handleAddRepairImage}
                    handleAddAssessImage={handleAddAssessImage}
                    handleAddReviewImage={handleAddReviewImage}
                    removeIssueImage={removeIssueImage}
                    removeRepairImage={removeRepairImage}
                    removeAssessImage={removeAssessImage}
                    removeReviewImage={removeReviewImage}
                    onShowIssueImageViewer={turnOnIssueImageViewer}
                    isCheckedStep={isCheckedStep}
                    setIsCheckedStep={setIsCheckedStep}
                    currentIssue={currentIssue}
                    assignedUserId={attempt.assignedUserId}
                    isTask={isTask}
                  />
                </Box>
              )}
              {view === VIEWS.MATERIAL_INVOICE && isMiDayFeatureEnabled && (
                <CloseoutMaterial
                  address={attempt.home?.address}
                  communityName={attempt.home?.communityName}
                  dispatcherName={attempt.dispatcherName}
                  materials={materials}
                  orderNumber={attempt.assignment?.assignmentNumber}
                  orderType={attempt.assignment?.assignmentTemplateName}
                  issues={issues}
                  attemptId={attempt.id}
                  assignmentId={attempt.assignment?.id}
                  getAttempt={getAttempt}
                  isLoading={isLoading}
                  tenant={tenant}
                  technicianName={
                    attempt.assignedUserName ||
                    users.find((user) => user.id === attempt.assignedUserId)
                      ?.name
                  }
                  performedOn={
                    attempt.effectiveEndDate ||
                    attempt.endDate ||
                    attempt.modifiedOn
                  }
                  setIsMaterialChecked={setIsMaterialChecked}
                />
              )}
            </Box>
          </Box>
          <Box className={classes.footer}>
            <MiButton
              className={classes.cancelBtn}
              title="Cancel"
              variant={BUTTON_VARIANTS.TEXT}
              onClick={onCancel}
              style={{
                textDecoration: "underline",
                textUnderlineOffset: theme.spacing(1),
              }}
            />
            {issueIndex !== 0 && (
              <MiButton
                title={`Return: ${getReturnLabel(
                  verificationReview,
                  readOnly
                )} ${issueIndex}/${issueCount}`}
                variant={BUTTON_VARIANTS.OUTLINED}
                className={classes.confirmBtn}
                disabled={isAddingImages}
                onClick={() => {
                  setIsCheckedStep(true);
                  scrollToTop();
                  if (view === VIEWS.MATERIAL_INVOICE) {
                    setView(VIEWS.ISSUE_REVIEW);
                    const lastIssueIndex = issues.length - 1;
                    setIssueIndex(lastIssueIndex);
                    return;
                  }
                  setIssueIndex((prev) => prev - 1);
                }}
              />
            )}
            {verificationReview &&
              isLastOne &&
              !selectedIssue &&
              !attempt?.verifiedBy && (
                <MiButton
                  title="Complete Review"
                  disabled={!attempt.pendingVerification || isAddingImages}
                  variant={BUTTON_VARIANTS.OUTLINED}
                  className={classes.confirmBtn}
                  onClick={handleCompleteClick}
                />
              )}
            {(!readOnly || (readOnly && !isLastOne)) && (
              <MiButton
                title={getNextButtonText()}
                disabled={isNextButtonDisabled}
                loading={isActionLoading}
                variant={BUTTON_VARIANTS.CONTAINED}
                className={classes.confirmBtn}
                onClick={onSubmit}
              />
            )}
          </Box>
        </Box>
        <DocumentViewer
          documents={imagesInViewer}
          onUnfullscreen={() => {
            setShowImagesViewer(false);
          }}
          fullScreenStatus={showImagesViewer}
          onDelete={handleDeleteDocument}
        />
        <MiModalConfirm
          {...confirmModalConfig}
          sx={{ zIndex: theme.zIndex.popper }}
          loading={loading}
        />
      </>
    );
  }
);

const useStyles = makeStyles()((theme, { history = true }) => ({
  contentContainer: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  header: {
    display: "flex",
  },
  headerLeftBox: {
    backgroundColor: theme.palette.red.main,
    flex: 1,
    padding: theme.spacing(2),
    color: theme.palette.common.white,
  },
  headerRightBox: {
    backgroundColor: theme.palette.orange.main,
    flex: 1,
    padding: theme.spacing(2),
    color: theme.palette.common.white,
  },
  content: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    backgroundColor: theme.palette.grey.mediumLight,
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: history
      ? theme.spacing(0, 0, 0.5, 0.5)
      : theme.shape.borderRadius,
  },
  footer: {
    backgroundColor: theme.palette.common.white,
    position: "sticky",
    bottom: 0,
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(4),
    padding: theme.spacing(2, 0),
    zIndex: theme.zIndex.drawer,
  },
  cancelBtn: {
    color: theme.palette.common.black,
    fontWeight: theme.typography.fontWeightBold,
  },
  confirmBtn: {
    fontWeight: theme.typography.fontWeightBold,
  },
}));
